<script lang="ts">
  /**
   * Stacked bar chart: custo diário do processo de ponto.
   * Empilha 2 componentes da mesma ordem de grandeza: hora extra + atrasos.
   * Correções manuais saem em gráfico dedicado (escala muito menor — viraria
   * sliver invisível aqui).
   */
  import type { DailyAnalyticsPoint } from '$core/metrics/metrics.types';
  import {
    makeGeometry,
    formatDateShort,
    formatBrlCompact,
    niceMax,
    thinLabels,
  } from './chartUtils';

  export let daily: DailyAnalyticsPoint[];
  export let width: number = 720;
  export let height: number = 240;

  const geom = makeGeometry(width, height, { left: 56 });
  const TICKS = [0, 0.25, 0.5, 0.75, 1];

  function mainCostOf(d: DailyAnalyticsPoint): number {
    return d.costOvertimeBrl + d.costAtrasoBrl;
  }

  $: model = (() => {
    const maxValue = niceMax(Math.max(0, ...daily.map(mainCostOf)));
    const baseY = geom.padding.top + geom.innerHeight;
    const stepX = daily.length > 1 ? geom.innerWidth / (daily.length - 1) : geom.innerWidth;
    const bandWidth = Math.max(2, daily.length > 0 ? geom.innerWidth / daily.length * 0.78 : 0);

    const toX = (i: number) => daily.length <= 1
      ? geom.padding.left + geom.innerWidth / 2
      : geom.padding.left + i * stepX;
    const toH = (v: number) => maxValue > 0 ? Math.min(1, v / maxValue) * geom.innerHeight : 0;
    const toY = (v: number) => baseY - toH(v);

    const bars = daily.map((d, i) => {
      const overtimeH = toH(d.costOvertimeBrl);
      const atrasoH = toH(d.costAtrasoBrl);
      const xCenter = toX(i);
      return {
        date: d.date,
        x: xCenter - bandWidth / 2,
        labelX: xCenter,
        overtime: { y: baseY - overtimeH, h: overtimeH, total: d.costOvertimeBrl },
        atraso: { y: baseY - overtimeH - atrasoH, h: atrasoH, total: d.costAtrasoBrl },
      };
    });

    const linePath = daily
      .map((d, i) => `${i === 0 ? 'M' : 'L'} ${toX(i).toFixed(2)} ${toY(mainCostOf(d)).toFixed(2)}`)
      .join(' ');

    return {
      maxValue,
      bandWidth,
      bars,
      linePath,
      gridlines: TICKS.map(t => ({ t, y: toY(maxValue * t), label: formatBrlCompact(maxValue * t) })),
    };
  })();

  $: total = daily.reduce((acc, d) => acc + mainCostOf(d), 0);
  $: avg = daily.length > 0 ? total / daily.length : 0;
  $: peak = daily.reduce<DailyAnalyticsPoint | null>((best, d) => {
    if (!best || mainCostOf(d) > mainCostOf(best)) return d;
    return best;
  }, null);
  $: showLabel = thinLabels(daily, 8);
</script>

<div class="chart">
  <header class="chart__head">
    <div>
      <h3 class="chart__title">Custo diário: hora extra + atrasos</h3>
      <p class="chart__subtitle">Componentes de mesma ordem de grandeza, dia a dia</p>
    </div>
    <div class="chart__kpis">
      <div class="kpi">
        <span class="kpi__label">Total no período</span>
        <span class="kpi__value">{formatBrlCompact(total)}</span>
      </div>
      <div class="kpi">
        <span class="kpi__label">Média/dia</span>
        <span class="kpi__value">{formatBrlCompact(avg)}</span>
      </div>
      {#if peak}
        <div class="kpi">
          <span class="kpi__label">Pico ({formatDateShort(peak.date)})</span>
          <span class="kpi__value kpi__value--alert">{formatBrlCompact(peak.costOvertimeBrl + peak.costAtrasoBrl)}</span>
        </div>
      {/if}
    </div>
  </header>

  <svg viewBox="0 0 {width} {height}" preserveAspectRatio="none" role="img" aria-label="Custo diário do processo de ponto">
    {#each model.gridlines as g}
      <line
        x1={geom.padding.left}
        x2={geom.padding.left + geom.innerWidth}
        y1={g.y}
        y2={g.y}
        stroke="#E7E7E7"
        stroke-dasharray={g.t === 0 ? '0' : '2 4'}
        stroke-width="1"
      />
      <text x={geom.padding.left - 8} y={g.y + 4} font-size="10" text-anchor="end" fill="#7D7D7D">{g.label}</text>
    {/each}

    {#each model.bars as bar}
      {#if bar.overtime.h > 0}
        <rect x={bar.x} y={bar.overtime.y} width={model.bandWidth} height={bar.overtime.h} fill="#FB7224" rx="1.5" />
      {/if}
      {#if bar.atraso.h > 0}
        <rect x={bar.x} y={bar.atraso.y} width={model.bandWidth} height={bar.atraso.h} fill="#7F3D7D" rx="1.5" />
      {/if}
    {/each}

    <path d={model.linePath} fill="none" stroke="#FB7224" stroke-width="1.6" stroke-linejoin="round" opacity="0.55" />

    {#each daily as d, i}
      {#if showLabel[i]}
        <text
          x={model.bars[i]?.labelX ?? 0}
          y={geom.padding.top + geom.innerHeight + 16}
          font-size="10"
          text-anchor="middle"
          fill="#565656"
        >{formatDateShort(d.date)}</text>
      {/if}
    {/each}
  </svg>

  <ul class="legend" aria-label="Legenda">
    <li><span class="legend__swatch" style="background: #FB7224"></span>Hora extra</li>
    <li><span class="legend__swatch" style="background: #7F3D7D"></span>Atrasos</li>
    <li class="legend__note">Correções manuais ficam no gráfico abaixo (escala dedicada).</li>
  </ul>
</div>

<style>
  .chart { display: flex; flex-direction: column; gap: var(--ptt-space-3); }
  .chart__head { display: flex; justify-content: space-between; gap: var(--ptt-space-4); flex-wrap: wrap; align-items: flex-end; }
  .chart__title { margin: 0; font-size: 1rem; font-weight: 700; color: var(--ptt-gray-300); }
  .chart__subtitle { margin: 2px 0 0; font-size: 0.8125rem; color: var(--ptt-gray-100); }
  .chart__kpis { display: flex; gap: var(--ptt-space-4); flex-wrap: wrap; }
  .kpi { display: flex; flex-direction: column; align-items: flex-start; line-height: 1.1; }
  .kpi__label { font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--ptt-gray-100); }
  .kpi__value { font-size: 1.125rem; font-weight: 700; color: var(--ptt-gray-300); margin-top: 2px; }
  .kpi__value--alert { color: var(--ptt-orange-100); }
  svg { width: 100%; height: auto; display: block; }
  .legend { list-style: none; margin: 0; padding: 0; display: flex; gap: var(--ptt-space-4); flex-wrap: wrap; font-size: 0.75rem; color: var(--ptt-gray-200); }
  .legend li { display: inline-flex; align-items: center; gap: var(--ptt-space-2); }
  .legend__note { font-style: italic; color: var(--ptt-gray-100); }
  .legend__swatch { width: 12px; height: 12px; border-radius: 3px; display: inline-block; }
</style>
